import React, { useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import quillEmoji from 'quill-emoji';
import Select from "react-select";
import "quill-emoji/dist/quill-emoji.css";
import "quill/dist/quill.snow.css";
import { Button } from "reactstrap";
import QRCode from "react-qr-code";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "store";
import moment from "moment";
import { isJson, replacePlaceholders } from "utils";
import makeAnimated from 'react-select/animated';
import { Action, ThunkDispatch, createSelector } from "@reduxjs/toolkit";
import { optionsListGetRequest } from "store/auth/action";

const animatedComponents = makeAnimated();

// Quill Emoji setup
const { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } = quillEmoji;
Quill.register({
    'formats/emoji': EmojiBlot,
    'modules/emoji-shortname': ShortNameEmoji,
    'modules/emoji-toolbar': ToolbarEmoji,
    'modules/emoji-textarea': TextAreaEmoji
}, true);

type MessageAckType = "-1" | "0" | "1" | "2" | "3" | "4";

const MessageAck: Record<MessageAckType, { icon: string; color: string }> = {
    "-1": { icon: "ri-close-fill", color: "text-danger" },
    "0": { icon: "ri-time-line", color: "text-warning" },
    "1": { icon: "ri-check-line", color: "text-default" },
    "2": { icon: "ri-check-double-line", color: "text-default" },
    "3": { icon: "ri-check-double-line", color: "text-info" },
    "4": { icon: "ri-play-line", color: "text-info" }
};

interface Message {
    ack?: MessageAckType;
    body: string;
    fromMe: boolean;
    type: string;
    timestamp: number;
    [key: string]: any;
}

interface ChatbotProps {
    onClose: (state: boolean) => void;
    info?: any; // Optional prop for additional info
    model?: string; // Optional prop for model
}

const Chatbot: React.FC<ChatbotProps> = ({ onClose, info, model }) => {
    const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
    const user = useSelector((state: ApplicationState) => state.auth.userProfile);
    const studentDetails = useSelector((state: ApplicationState) => state.student.details);
    const application = useSelector((state: ApplicationState) => state.applications.details);
    const course = useSelector((state: ApplicationState) => state.courses.details);
    const optionsObj = useSelector((state: ApplicationState) => state.auth.optionData);
    const socketRef = useRef<any>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const selectDashboardData = createSelector(
        (state: any) => state.Layout,
        (state) => ({
            layoutModeType: state.layoutModeType
        })
    );
    const optionsLoading = useSelector((state: ApplicationState) => state.auth.optionsLoading);
    const { layoutModeType } = useSelector(selectDashboardData);
    const [waProgress, setWaProgress] = useState<"INIT" | "QR" | "AUTH" | "LOADING" | "READY" | "FAIL">("INIT");
    const [socketConnected, setSocketConnected] = useState(false);
    const [qr, setQr] = useState<string>("");
    const [student, setStudent] = useState<any>(null);
    const [message, setMessage] = useState<string>("");
    const [messages, setMessages] = useState<Message[]>([]);
    const [contactId, setContactId] = useState<string | null>(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(false);
    const [waError, setWAError] = useState(false);
    const [waAuthError, setWAAuthError] = useState(false);
    const [whatsappTemplate, setWhatsappTemplate] = useState<any>(null);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
    const [sameNumberError, setSameNumberError] = useState(false);
    const [options, setOptions] = useState<any>([]);

    const customStyles = {
        input: (provided: any) => ({
            ...provided,
            color: layoutModeType === 'light' ? 'black' : 'white',
        }),
        control: (provided: any) => ({
            ...provided,
            backgroundColor: 'transparent'
        }),
        menu: (provided: any) => ({
            ...provided,
            backgroundColor: layoutModeType === 'light' ? 'white' : '#495057',
            color: layoutModeType === 'light' ? 'black' : 'white',
            zIndex: 9999
        }),
        option: (provided: any) => ({
            ...provided,
            backgroundColor: layoutModeType === 'light' ? 'white' : '#495057',
            color: layoutModeType === 'light' ? 'black' : 'white',
            '&:hover': {
                backgroundColor: layoutModeType === 'light' ? 'white' : '#495057',
            },
        }),
    };

    useEffect(() => {
        if (!socketConnected && user?.id && (student?.whatsapp_number || student?.mobile)) {
            const socket = io("https://api.dts.whatsapp.zilter.io", { transports: ["websocket"], query: { tenantId: user?.tenant?.id } });
            socketRef.current = socket;
            setSocketConnected(true);

            if (user?.phoneNumber === (student?.whatsapp_number || student?.mobile)) {
                setSameNumberError(true);
            } else {
                setWaProgress("INIT");
                socket.emit("create_session", { id: user.id });

                socket.on("qr", ({ qr }: { qr: string }) => {
                    // Delay QR rendering to avoid premature display during recovery
                    setTimeout(() => {
                        if (!ready && !waError && !waAuthError) {
                            setQr(qr);
                            setWaProgress("QR");
                        }
                    }, 1500); // 1.5s debounce to give time for recovery
                });

                socket.on("auth_failure", () => {
                    if (!ready) {
                        setWaProgress("FAIL");
                        setWAAuthError(true);
                    }
                });

                socket.on("authenticated", () => {
                    setWaProgress("AUTH");
                });

                socket.on("loading_screen", () => {
                    setWaProgress("LOADING");
                });

                socket.on("ready", ({ client }: { client: any }) => {
                    setWaProgress("READY");

                    const number = user?.phoneNumber?.replace(/\D/g, "");
                    const studentNumber = student?.whatsapp_number
                        ? student?.whatsapp_number.replace(/\D/g, "")
                        : student?.mobile?.replace(/\D/g, "");

                    if (number === client?.me?.user) {
                        setQr("");
                        socket.emit("get_chats", { id: user.id, number: studentNumber });
                    } else {
                        setQr("");
                        setError(true);
                        socket.emit("destroy_client", { id: user.id });
                    }
                });

                socket.on("wa_not_registered", () => {
                    if (!ready) {
                        setWaProgress("FAIL");
                        setWAError(true);
                    }
                });

                socket.on("message_ack", ({ message }: { message: any }) => {
                    const { id, ack, tempId } = message;

                    setMessages(prevMessages =>
                        prevMessages.map(m => {
                            const mId = (m.id?._serialized || m.id);
                            if (mId === id || (tempId && mId === tempId)) {
                                return { ...m, ack: ack };
                            }
                            return m;
                        })
                    );
                });


                socket.on("all_chats", ({ messages, contactId }: { messages: any[]; contactId: string }) => {
                    setReady(true);
                    setMessages([...messages]);
                    setContactId(contactId);
                });

                socket.on("new_message", ({ message }: { message: any }) => {
                    if (!message) return;

                    const studentNumber = student?.whatsapp_number
                        ? student?.whatsapp_number.replace(/\D/g, "")
                        : student?.mobile?.replace(/\D/g, "");

                    const messageContactId = message.contactId;
                    const messageNumber = messageContactId?.replace(/\D/g, "").replace(/@c\.us$/, "");

                    if (messageNumber === studentNumber) {
                        setMessages(prev => [...prev, { ...message }]);
                    } else {
                        console.log("⚠️ Ignored message from different contact:", messageNumber);
                    }
                });
            }
        }

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocketConnected(false);
            }
        };
    }, [user?.id, student?.whatsapp_number, student?.mobile]);

    useEffect(() => {
        if (studentDetails?.valuesJson) {
            setStudent(studentDetails.valuesJson);
        }
    }, [studentDetails]);

    useEffect(() => {
        if (optionsObj && Array.isArray(optionsObj['templates'])) {

            const newOptions = optionsObj['templates']
                .map((option: any) => {
                    const optionsJson = option.valuesJson && isJson(option.valuesJson)
                        ? JSON.parse(option.valuesJson)
                        : option.valuesJson;

                    return {
                        label: optionsJson?.name,
                        value: option.id,
                        valuesJson: optionsJson,
                        type: optionsJson?.type, // e.g., "email" or "whatsapp"
                        in_forms: true,
                    };
                })
                .filter((opt) => opt.type === "whatsapp"); // 👈 Adjust the type filter as needed
            setOptions(newOptions);
        }
    }, [optionsObj?.['templates']]);


    const handleTemplateSelect = (template: any) => {
        const rawBody = template?.valuesJson?.template || "";
        const rawSubject = template?.valuesJson?.subject || "";

        const filledBody = replacePlaceholders(rawBody, templateContext);
        const filledSubject = replacePlaceholders(rawSubject, templateContext);

        setMessage(filledBody);     // Set in ReactQuill
        setWhatsappTemplate(template?.name);  // Optional if you show subject
    };

    const templateContext = model === "students" ? {
        students: info,
        student: info
    } : {
        students: info?.student,
        student: info?.student,
        application: info,
        applications: info,
        course,
    }

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
    };

    const messageACK = (message: any) => {
        const messageId = message.id._serialized;
        setMessages((prevMessages) =>
            prevMessages.map((m) => {
                if (m.id && m.id._serialized === messageId) {
                    return { ...m, ack: message.ack }; // update ack
                }
                return m;
            })
        );
        setTimeout(() => {
            scrollToBottom();
        }, 100);
    };


    const handleClose = () => {
        if (user?.id && socketRef.current) {
            socketRef.current.emit("destroy_client", { id: user.id });
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocketConnected(false);
        }
        onClose(false);
    };

    const onFocus = () => {
        setOptions([]);
        const filter = {
            "filters": [
                { "quick": [], "advance": [], "search": null }
            ],
            "sorts": [
                { "field": "name", "order": "asc" }
            ]
        };
        let filterObject = null;
        dispatch(optionsListGetRequest('templates', filter, 0, 20, filterObject));
    };

    const getStatus = (state: string) => {
        const statusMap: Record<string, string> = {
            INIT: "Initializing WhatsApp...",
            QR: "Waiting for QR scan...",
            AUTH: "Authenticating...",
            LOADING: "Loading chats...",
            READY: "Chat ready!",
            FAIL: "Failed to connect."
        };
        return statusMap[state] || "Connecting...";
    };

    const onRegenerate = () => {
        setError(false);
        setWAError(false);
        setWAAuthError(false);
        setSameNumberError(false);
        setQr("");
        setWaProgress("INIT");
        if (socketRef.current && user?.id) {
            socketRef.current.emit("create_session", { id: user.id });
        }
    };
    const renderErrorState = () => {
        if (waAuthError) {
            return (
                <div className="chatboat-error">
                    <i className="ri-error-warning-fill fs-36 text-danger"></i>
                    <h3>Authentication Failed!</h3>
                    <p>The scanned number could not be authenticated. Please try again.</p>
                    <Button onClick={onRegenerate} color="primary" className="btn-label">
                        <i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i>
                        Regenerate QR
                    </Button>
                </div>
            );
        }

        if (waError) {
            return (
                <div className="chatboat-error">
                    <i className="ri-error-warning-fill fs-36 text-danger"></i>
                    <h3>Number Not Registered!</h3>
                    <p>The student's number is not linked to WhatsApp. Please verify the number and try again.</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="chatboat-error">
                    <i className="ri-error-warning-fill fs-36 text-danger"></i>
                    <h3>Number Mismatch!</h3>
                    <p>The scanned number does not match the account. Please use a registered number.</p>
                    <Button onClick={onRegenerate} color="primary" className="btn-label">
                        <i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i>
                        Regenerate QR
                    </Button>
                </div>
            );
        }

        if (sameNumberError) {
            return (
                <div className="chatboat-error">
                    <i className="ri-error-warning-fill fs-36 text-danger"></i>
                    <h3>Invalid number!</h3>
                    <p>You cannot start a chat with your own number.</p>
                </div>
            );
        }

        return null;
    };

    const renderChatView = () => {
        const errorView = renderErrorState();
        if (errorView) {
            return errorView;
        }
        if (ready && messages.length === 0) {
            return (
                <div className="text-center text-muted p-5">
                    <i className="ri-chat-3-line fs-40 mb-3"></i>
                    <h5>No messages yet</h5>
                    <p>Start the conversation!</p>
                </div>
            );
        }
        if (ready) {
            return (
                <div className="direct-chat-messages d-flex flex-column gap-3">
                    {messages.map((message, index) => {
                        const ackValue: MessageAckType = message.fromMe
                            ? message.ack || "0"
                            : "1"; // ✅ Always single tick for received messages

                        const ack = MessageAck[ackValue];
                        const ackIcon = <i className={`${ack.icon} ${ack.color} fs-16`} />;

                        const isFromMe = message.fromMe;

                        const bgClass = isFromMe
                            ? "bg-primary text-white"
                            : "bg-body-secondary text-body"; // ✅ Works in light/dark mode

                        return (
                            <div
                                key={message.id?._serialized || message.id || index}
                                className={`d-flex ${isFromMe ? "justify-content-end" : "justify-content-start"} mb-3`}
                            >
                                <div
                                    className={`p-3 shadow-sm rounded-4 ${bgClass}`}
                                    style={{ maxWidth: "75%" }}
                                >
                                    <div dangerouslySetInnerHTML={{ __html: convertWhatsAppToHtml(message.body) }} />
                                    <div className={`text-end small mt-1`}>
                                        {moment.unix(message.timestamp).format("MMM Do, h:mm a")} {ackIcon}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef}></div>
                </div>
            );
        }

        if (qr && waProgress === "QR") {
            return (
                <div className="d-flex flex-column align-items-center p-4 justify-content-center text-center">
                    <h3 className="text-primary">Scan the QR Code</h3>
                    <p>Open WhatsApp → Settings → Linked Devices → Scan QR</p>
                    <div className="border rounded p-3 shadow-sm mt-3 bg-white">
                        <QRCode fgColor="#3b4a54" value={qr} size={200} />
                    </div>
                </div>
            );
        }

        return (
            <div className="d-flex flex-column align-items-center justify-content-center p-4 text-center" style={{ minHeight: "400px" }}>
                <i className="ri-whatsapp-line display-1 shimmer"></i>
                <h5 className="mt-4">{getStatus(waProgress)}</h5>
            </div>
        );
    };

    const convertWhatsAppToHtml = (text: string): string => {
        if (!text) return "";
        return text
            .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
            .replace(/_(.*?)_/g, "<em>$1</em>")
            .replace(/~(.*?)~/g, "<s>$1</s>")
            .replace(/```(.*?)```/g, "<pre>$1</pre>")
            .replace(/^- (.*?)(?=\n|$)/gm, "<li>$1</li>")
            .replace(/(?:^|\n)(<li>.*?<\/li>)(?:\n|$)/g, "<ul>$1</ul>")
            .replace(/\n/g, "<br>");
    };

    const convertHtmlToWhatsApp = (html: string): string => {
        if (!html) return "";

        return html
            .replace(/&nbsp;/g, " ")
            .replace(/<strong>(.*?)<\/strong>/g, "*$1*")
            .replace(/<em>(.*?)<\/em>/g, "_$1_")
            .replace(/<s>(.*?)<\/s>/g, "~$1~")
            .replace(/<pre>(.*?)<\/pre>/g, "```$1```")
            .replace(/<ul>(.*?)<\/ul>/g, (_, c) => c.replace(/<li>(.*?)<\/li>/g, "- $1"))
            .replace(/<br\s*\/?>/g, "\n")
            .replace(/<\/?[^>]+(>|$)/g, "");
    };

    const onSend = () => {
        if (message.trim()) {
            const formattedMessage = convertHtmlToWhatsApp(message);
            if (formattedMessage && socketRef.current) {
                const tempId = `local-${Date.now()}`;

                socketRef.current.emit("send_message", {
                    id: user.id,
                    message: formattedMessage,
                    contactId,
                    tempId
                });

                setMessages((prev) => [
                    ...prev,
                    {
                        id: tempId, //Save tempId here
                        ack: "0",
                        body: formattedMessage,
                        fromMe: true,
                        type: "chat",
                        timestamp: moment().unix(),
                    },
                ]);
                setMessage("");
            }
        }
    };

    const onInputChange = (value: any) => {
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }
        const newTimeout = setTimeout(() => {
            const filter = {
                "filters": [
                    { "search": { "term": value, "field": "all" } }
                ],
                "sorts": [
                    { "field": "name", "order": "asc" }
                ]
            };
            let filterObject = null;
            dispatch(optionsListGetRequest('templates', filter, 0, 100, filterObject));
        }, 2000);
        setTypingTimeout(newTimeout);
    };

    return (
        <div className="dts-chatbot">
            <div className="row container d-flex justify-content-center">
                <div className="col-md-12">
                    <div className="box border-top-1 border-primary direct-chat direct-chat-warning position-relative border-radius-1 shadow-lg bg-light-subtle">
                        <div className="box-header d-flex align-items-center justify-content-between shadow">
                            <h3 className="box-title">WhatsApp Messages</h3>
                            <Button color="light" className="btn-icon btn-ghost-dark" onClick={handleClose}>
                                <i className="ri-close-fill" />
                            </Button>
                        </div>
                        <div className="whatsapp-background">
                            <div className="box-body p-3">{renderChatView()}</div>
                            {ready && !(error || waError) ? (
                                <div className="bg-body rounded-4 shadow-sm d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center pe-2">
                                        <div id="quill-toolbar">
                                            <span className="ql-formats">
                                                <button className="ql-bold" />
                                                <button className="ql-italic" />
                                                <button className="ql-strike" />
                                            </span>
                                            <span className="ql-formats">
                                                <button className="ql-list" value="bullet" />
                                            </span>
                                            <span className="ql-formats">
                                                <button className="ql-emoji" />
                                            </span>
                                        </div>
                                        <div style={{ width: "300px" }}>
                                            <Select
                                                placeholder="Select template..."
                                                onFocus={onFocus}
                                                closeMenuOnSelect={true}
                                                components={animatedComponents}
                                                isClearable
                                                isSearchable
                                                styles={customStyles}
                                                onInputChange={(inputValue: any) => onInputChange(inputValue)}
                                                name="values"
                                                onChange={handleTemplateSelect}
                                                value={whatsappTemplate}
                                                isLoading={optionsLoading}
                                                menuPlacement="auto"
                                                menuPosition="fixed"
                                                options={optionsLoading ? [{ label: 'Loading...', value: null }] : options || []}
                                            />
                                        </div>
                                    </div>
                                    <ReactQuill
                                        placeholder="Compose a message..."
                                        theme="snow"
                                        value={message}
                                        onChange={setMessage}
                                        modules={{
                                            toolbar: { container: "#quill-toolbar" },
                                            "emoji-toolbar": true,
                                            "emoji-textarea": false,
                                            "emoji-shortname": true,
                                        }}
                                        formats={["bold", "italic", "strike", "list", "bullet", "emoji"]}
                                        className="shadow-sm rounded-4"
                                    />

                                    <Button
                                        size="sm"
                                        disabled={!message.trim() || error || waError || !!qr}
                                        color="primary"
                                        className="btn-label right"
                                        onClick={onSend}
                                    >
                                        <i className="ri-send-plane-fill label-icon align-middle fs-16 ms-2"></i> Send
                                    </Button>
                                </div>
                            ) : (
                                <div className="box-footer p-4"></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Chatbot;